import { ReactNode } from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CommonTooltip from '~/components/CommonTooltip';
import useResponsive from '~/hooks/useResponsive';

interface InfoCardPropsBase {
  title: string;
  primaryItem?: ReactNode;
  minHeight?: number;
  isLoading?: boolean;
}

interface TooltipOnly extends InfoCardPropsBase {
  tooltipText: string;
  secondaryItem?: never;
}

interface ActionOnly extends InfoCardPropsBase {
  tooltipText?: never;
  secondaryItem: {
    itemNode: ReactNode;
    type?: 'button';
  };
}

type InfoCardProps = TooltipOnly | ActionOnly;

const InfoCard: React.FC<InfoCardProps> = ({
  title,
  primaryItem,
  minHeight = 68,
  tooltipText,
  secondaryItem,
  isLoading = false,
}) => {
  const { isMobile } = useResponsive();
  const isMobileView = isMobile();

  const showTooltip = Boolean(tooltipText);
  const { type: itemType, itemNode } = secondaryItem || {};
  const showSecondary = !!itemNode;

  if (isLoading) {
    return (
      <Card sx={{ pt: 0 }}>
        <CardContent>
          <Stack direction="column" spacing={2}>
            <Skeleton variant="text" width={100} />
            <Skeleton variant="text" width={100} />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ pt: 0 }}>
      <CardContent>
        <Stack
          direction={itemType === 'button' && isMobileView ? 'column' : 'row'}
          justifyContent="space-between"
          spacing={{ sm: 2 }}
        >
          <Stack
            direction="column"
            justifyContent={
              itemType === 'button' && isMobileView
                ? 'flex-start'
                : 'space-between'
            }
            minHeight={minHeight}
            spacing={1}
            flex={1}
          >
            <Typography variant="h3">{title}</Typography>
            {primaryItem}
          </Stack>
          {showTooltip ? (
            <Stack>
              <CommonTooltip title={tooltipText}>
                <IconButton sx={{ p: 0 }}>
                  <InfoOutlinedIcon />
                </IconButton>
              </CommonTooltip>
            </Stack>
          ) : (
            showSecondary && <Stack justifyContent="center">{itemNode}</Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default InfoCard;
